import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  Show,
} from "solid-js";

import { Floating } from "@uic/adapters/floating-ui";

/** Keys that move the highlight while the menu is open. */
const NAVIGATION_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End"]);

/** Keys that open the menu, or pick the highlighted option once it is open. */
const ACTIVATION_KEYS = new Set(["Enter", " "]);

/**
 * Where a navigation key moves the highlight, stopping at either end.
 * @param {string} key Key pressed
 * @param {number} index Currently highlighted index
 * @param {number} count Number of options
 * @returns {number} Newly highlighted index
 */
function moveHighlight(key, index, count) {
  switch (key) {
    case "ArrowDown":
      return Math.min(index + 1, count - 1);
    case "ArrowUp":
      return Math.max(index - 1, 0);
    case "Home":
      return 0;
    default:
      return count - 1;
  }
}

/**
 * A select-only combobox. Focus stays on the trigger while the menu is open,
 * and the arrow keys move a highlight that `aria-activedescendant` announces.
 * @template T
 * @param {object} props The component props
 * @param {T} props.value The current selected value
 * @param {T[]} props.options Array of options
 * @param {(option: T) => import('solid-js').JSX.Element} props.renderOption Function to render each option
 * @param {(option: T) => import('solid-js').JSX.Element} [props.renderSelected] Function to render selected value (defaults to renderOption)
 * @param {(value: T) => void} [props.onChange] Callback when value changes
 * @param {string} [props.className] Additional classes for container
 * @param {'xs' | 'sm' | 'md' | 'lg'} [props.size] Control size
 * @param {string} [props.label] Label text
 * @returns {import('solid-js').JSX.Element} generic select dropdown
 */
export function UISelect(props) {
  const [isOpen, setIsOpen] = createSignal(false);
  const [container, setContainer] = createSignal(null);
  const [highlightedIndex, setHighlightedIndex] = createSignal(0);
  const listboxId = createUniqueId();
  const labelId = createUniqueId();
  const optionId = (index) => `${listboxId}-${index}`;

  const sizeClasses = {
    xs: "tw:d-select-xs",
    sm: "tw:d-select-sm",
    md: "tw:d-select-md",
    lg: "tw:d-select-lg",
  };

  const textClasses = {
    xs: "tw:text-meta",
    sm: "tw:text-meta",
    md: "tw:text-dense",
    lg: "tw:text-body",
  };

  const handleSelect = (option) => {
    props.onChange?.(option);
    setIsOpen(false);
  };

  const openMenu = () => {
    setHighlightedIndex(Math.max(props.options.indexOf(props.value), 0));
    setIsOpen(true);
  };

  const handleTriggerClick = () => {
    if (isOpen()) setIsOpen(false);
    else openMenu();
  };

  const handleTriggerKeyDown = (e) => {
    const count = props.options.length;
    if (!isOpen()) {
      if (!NAVIGATION_KEYS.has(e.key) && !ACTIVATION_KEYS.has(e.key)) return;
      e.preventDefault();
      openMenu();
      return;
    }

    if (NAVIGATION_KEYS.has(e.key)) {
      e.preventDefault();
      setHighlightedIndex(moveHighlight(e.key, highlightedIndex(), count));
    } else if (ACTIVATION_KEYS.has(e.key)) {
      e.preventDefault();
      const option = props.options[highlightedIndex()];
      if (option !== undefined) handleSelect(option);
    }
  };

  // A button fires its click for Space on keyup, which would reopen the menu
  // that keydown just closed.
  const handleTriggerKeyUp = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  createEffect(() => {
    if (!isOpen()) return;
    document
      .getElementById(optionId(highlightedIndex()))
      ?.scrollIntoView({ block: "nearest" });
  });

  const renderSelected = createMemo(
    () => props.renderSelected || props.renderOption,
  );

  const triggerClasses = createMemo(() =>
    [
      "tw:d-select",
      "tw:cursor-pointer",
      "tw:text-left",
      "tw:font-mono",
      sizeClasses[props.size || "md"],
      textClasses[props.size || "md"],
    ].join(" "),
  );

  const Trigger = () => (
    <button
      type="button"
      role="combobox"
      className={triggerClasses()}
      aria-haspopup="listbox"
      aria-expanded={isOpen()}
      aria-controls={isOpen() ? listboxId : undefined}
      aria-activedescendant={
        isOpen() && props.options.length > 0
          ? optionId(highlightedIndex())
          : undefined
      }
      aria-labelledby={props.label ? labelId : undefined}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      onKeyUp={handleTriggerKeyUp}
      onBlur={() => setIsOpen(false)}
    >
      {renderSelected()(props.value)}
    </button>
  );

  return (
    <div
      ref={setContainer}
      className={`tw:flex tw:w-full tw:flex-col ${props.className || ""}`}
    >
      <Show when={props.label} fallback={<Trigger />}>
        <div className="tw:d-floating-label tw:text-meta">
          <span id={labelId}>{props.label}</span>
          <Trigger />
        </div>
      </Show>
      <Show when={isOpen()}>
        <Floating
          reference={container()}
          placement="bottom-start"
          offset={4}
          matchWidth
          maxHeight={200}
          onDismiss={() => setIsOpen(false)}
          className="tw:z-50 tw:overflow-y-auto tw:border tw:border-base-content/20 tw:bg-base-100 tw:p-1 tw:shadow-lg"
        >
          <ul id={listboxId} role="listbox" className="tw:flex tw:flex-col">
            <For each={props.options}>
              {(option, index) => (
                <li role="none" className="tw:list-none">
                  <button
                    id={optionId(index())}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={option === props.value}
                    className="tw:w-full tw:cursor-pointer tw:p-2 tw:text-left tw:transition-colors"
                    classList={{
                      "tw:bg-base-200": index() === highlightedIndex(),
                    }}
                    onMouseEnter={() => setHighlightedIndex(index())}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    {props.renderOption(option)}
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Floating>
      </Show>
    </div>
  );
}
